/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from 'react';
import StyledDateField from './StyledDateField';
import moment, { type Moment } from 'moment';
import useIsPortfolioReadonly from '@/components/ChallengePortfolio/NormalView/useIsPortfolioReadonly';
import DatePicker from './DatePicker';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import useBlockPortfolioItemCloseWhenDirty from '@/components/ChallengePortfolio/NormalView/portfolio-item/useBlockPortfolioItemCloseWhenDirty';

type InnerDateFieldProps = {
  initialValue?: number | null;
  onSave: (timestamp: number | null) => Promise<void>;
  shouldDisableDate?: (date: Moment) => boolean;
  clearable?: boolean;
};

const InnerDateField = ({
  initialValue,
  onSave,
  shouldDisableDate,
  clearable,
}: InnerDateFieldProps) => {
  const portfolioIsReadonly = useIsPortfolioReadonly();
  const [date, setDate, dateHasChanged, reenableDateSync] = useComplexSwrState(
    moment(initialValue),
    {
      stringifyChangeCheck: true,
    },
  );
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(
    async (value?: Moment): Promise<void> => {
      // Attempt to use the date value passed to handleSave, but fallback to
      // date state for the "Save unsaved changes?" functionality when no
      // value is passed to handleSave
      const newDate = value ?? date;
      const timestamp = newDate.isValid() ? newDate.valueOf() : null;

      setSaving(true);

      await onSave(timestamp);

      setSaving(false);

      reenableDateSync();
    },
    [date],
  );

  useBlockPortfolioItemCloseWhenDirty(dateHasChanged, handleSave);

  return (
    <StyledDateField>
      <DatePicker
        value={date}
        onChange={dateAsMoment => setDate(dateAsMoment)}
        onSave={handleSave}
        shouldDisableDate={shouldDisableDate}
        clearable={clearable}
        disabled={portfolioIsReadonly || saving}
      />
    </StyledDateField>
  );
};

export default InnerDateField;
